import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient from '../../api/apiClient';
import HRISHeader from '../../components/hris/HRISHeader';
import HRISStats from '../../components/hris/HRISStats';
import DepartmentChart from '../../components/hris/DepartmentChart';
import StatusChart from '../../components/hris/StatusChart';
import HRISQuickActions from '../../components/hris/HRISQuickActions';
import RecentLeaves from '../../components/hris/RecentLeaves';
import { containerVariants, itemVariants } from '../../utils/animations';

interface DashboardData {
  totalEmployees?: number;
  activeEmployees?: number;
  departments?: number;
  pendingLeaves?: number;
  todayPresent?: number;
  todayAbsent?: number;
  pendingRecruitment?: number;
  departmentData?: any[];
  statusData?: any[];
  recentLeaves?: any[];
}

export default function HRISDashboard() {
  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    departments: 0,
    pendingLeaves: 0,
    todayPresent: 0,
    todayAbsent: 0,
    pendingRecruitment: 0,
  });
  const [departmentData, setDepartmentData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const data = await apiClient.get<DashboardData>('/hris/dashboard');
      setStats({
        totalEmployees: data.totalEmployees || 0,
        activeEmployees: data.activeEmployees || 0,
        departments: data.departments || 0,
        pendingLeaves: data.pendingLeaves || 0,
        todayPresent: data.todayPresent || 0,
        todayAbsent: data.todayAbsent || 0,
        pendingRecruitment: data.pendingRecruitment || 0,
      });
      setDepartmentData(data.departmentData || []);
      setStatusData(data.statusData || []);
      setRecentLeaves(data.recentLeaves || []);
    } catch (error) {
      // Error loading dashboard data
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-4 md:space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <HRISHeader />
      </motion.div>

      <motion.div variants={itemVariants}>
        <HRISStats stats={stats} />
      </motion.div>

      <motion.div
        className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6"
        variants={itemVariants}
      >
        <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.2 }}>
          <DepartmentChart data={departmentData} />
        </motion.div>
        <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.2 }}>
          <StatusChart data={statusData} />
        </motion.div>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6"
        variants={itemVariants}
      >
        <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
          <HRISQuickActions />
        </motion.div>
        <motion.div
          className="lg:col-span-2"
          whileHover={{ scale: 1.01 }}
          transition={{ duration: 0.2 }}
        >
          <RecentLeaves leaves={recentLeaves} />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
